import { wrapRequest } from '@applandeo/api-wrapper';
import { parseISO } from 'date-fns';
import { FormikProps, useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { ALERT_VARIANT, useAlertMessages } from '@/components/Global/AlertMessages/AlertMessagesProvider';
import { getRequestPreview } from '@/dataExchange/services/newRequest.service';
import { updateRequestData } from '@/dataExchange/services/requestsPage.service';
import { RequestNoRestrictionSummary, RequestWithRestrictionSummary } from '@/domain/newRequest';
import { AbsenceWithTypeData, DatePickerRange } from '@/domain/requestsPage';
import { useRequestsConverter } from '@/hooks/useRequests/useRequestsConverter';
import useUsedLimits from '@/hooks/useUsedLimits/useUsedLimits';
import useLoggedInUserData from '@/hooks/useUserData/useLoggedInUserData';
import useLimitsUsageLogic from '@/pages/NewRequest/useLimitsUsageLogic';
import { useSentryActions } from '@/sentry/hooks/useSentryActions';
import { SENTRY_ACTIONS } from '@/sentry/sentryActions';
import { normalizeDateForAPI } from '@/utils/date/date';

export enum UPDATE_REQUEST_FIELD {
    selectedRequestTypeId = 'selectedRequestTypeId',
    dateRange = 'dateRange',
    comment = 'comment',
}

export interface IUpdateRequestFields {
    [UPDATE_REQUEST_FIELD.selectedRequestTypeId]: string;
    [UPDATE_REQUEST_FIELD.dateRange]: DatePickerRange;
    [UPDATE_REQUEST_FIELD.comment]: string;
}

type EditRequestLogicProps = {
    singleRequestDetails: AbsenceWithTypeData;
    onCloseModal: () => void;
    getRequests: () => void;
};

const useEditRequestLogic = ({ singleRequestDetails, onCloseModal, getRequests }: EditRequestLogicProps) => {
    const getRequestPreviewWrapper = wrapRequest(getRequestPreview);
    const updateRequestDataWrapper = wrapRequest(updateRequestData);

    const { t } = useTranslation();
    const { createAlert } = useAlertMessages();
    const { _id: absenceId, userId, absenceTypeId, from, to, comment } = singleRequestDetails;
    const { limitsUsage } = useLimitsUsageLogic({ userId, includeArchived: false });
    const { convertRequestPreviewResponseDtoToRequestSummary } = useRequestsConverter();
    const { handleSentryAction } = useSentryActions();
    const { isLead: isCurrentUserLead } = useLoggedInUserData();
    const { getAllLimits } = useUsedLimits({ userId: isCurrentUserLead ? userId : undefined });

    const [requestSummary, setRequestSummary] = useState<RequestNoRestrictionSummary & RequestWithRestrictionSummary>();
    const [isLimitExceeded, setIsLimitExceeded] = useState(false);

    const initialValues: IUpdateRequestFields = useMemo(
        () => ({
            selectedRequestTypeId: absenceTypeId,
            dateRange: { startDate: parseISO(from), endDate: parseISO(to) },
            comment,
        }),
        [absenceTypeId, from, to, comment]
    );

    const updateAbsence = useCallback(
        (formValues: IUpdateRequestFields) => {
            const dto = prepareRequestBody(formValues);
            if (!dto) return;

            updateRequestDataWrapper
                .fetch({
                    absenceId,
                    userRequestBody: dto,
                })
                .onAnyError(error => {
                    createAlert(ALERT_VARIANT.error, t('error_absenceStatusAlreadyChanged_message'));
                    handleSentryAction(error, SENTRY_ACTIONS.ERROR_UPDATE_REQUEST_DATA);
                })
                .onSuccess(() => {
                    createAlert(ALERT_VARIANT.success, t('requests_alerts_absence_update_success'));
                    getRequests();
                    getAllLimits();
                    onCloseModal();
                });
        },
        [createAlert, t]
    );

    const formik: FormikProps<IUpdateRequestFields> = useFormik({
        initialValues: initialValues,
        validationSchema: Yup.object().shape({
            dateRange: Yup.object().test(
                'isRequired',
                t('newApplication_applicationDate_chooseDays_title'),
                dateRange => {
                    return !(!dateRange?.startDate || !dateRange?.endDate);
                }
            ),
        }),
        onSubmit: updateAbsence,
    });

    const getRequestSummary = useCallback(() => {
        const { values } = formik;
        const { startDate, endDate } = values?.dateRange;
        const dto = prepareRequestBody(values);

        if (!startDate || !endDate || !values?.selectedRequestTypeId || !dto) return;

        getRequestPreviewWrapper
            .fetch({
                userId,
                requestBody: dto,
            })
            .onSuccess(response => {
                const requestSummary = convertRequestPreviewResponseDtoToRequestSummary(response);

                setIsLimitExceeded(response?.isOverLimit);
                setRequestSummary(requestSummary);
            });
    }, [getRequestPreviewWrapper, formik, t]);

    useEffect(() => {
        getRequestSummary();
    }, [formik?.values]);

    return { formik, limitsUsage, requestSummary, isLimitExceeded, isLoading: updateRequestDataWrapper.isLoading };
};

const prepareRequestBody = (values: IUpdateRequestFields) => {
    if (!values.dateRange.endDate || !values.dateRange.startDate) return;

    return {
        from: normalizeDateForAPI(values.dateRange.startDate),
        to: normalizeDateForAPI(values.dateRange.endDate),
        absenceTypeId: values?.selectedRequestTypeId,
        comment: values?.comment,
    };
};

export default useEditRequestLogic;
